import { slugify } from "@repo/fika-shared/src/lib/slug"

export interface TocHeading {
  id: string
  text: string
  level: number
}

interface TiptapNode {
  type: string
  attrs?: Record<string, unknown>
  content?: TiptapNode[]
  text?: string
}

function extractText(node: TiptapNode): string {
  if (node.type === "text") return node.text ?? ""
  if (node.content) return node.content.map(extractText).join("")
  return ""
}

/**
 * Extract headings from Tiptap JSON content.
 * Tiptap heading level 1 renders as h2, level 2 as h3, level 3 as h4.
 * We extract levels 1-3 (h2-h4) for the table of contents.
 */
export function extractHeadingsFromContent(
  contentJson: string | null,
): TocHeading[] {
  if (!contentJson) return []

  let doc: TiptapNode
  try {
    doc = JSON.parse(contentJson)
  } catch {
    return []
  }

  const headings: TocHeading[] = []
  const usedIds = new Map<string, number>()

  function walk(node: TiptapNode) {
    if (node.type === "heading") {
      const tiptapLevel = (node.attrs?.level as number) ?? 1
      const htmlLevel = tiptapLevel + 1 // Tiptap level 1 → h2

      if (htmlLevel >= 2 && htmlLevel <= 4) {
        const text = extractText(node).trim()
        if (text) {
          let baseId = slugify(text)
          if (!baseId) baseId = `heading-${headings.length}`

          const count = usedIds.get(baseId) ?? 0
          usedIds.set(baseId, count + 1)

          const id = count === 0 ? baseId : `${baseId}-${count + 1}`
          headings.push({ id, text, level: htmlLevel })
        }
      }
    }

    if (node.content) {
      for (const child of node.content) {
        walk(child)
      }
    }
  }

  walk(doc)
  return headings
}

/**
 * Apply heading IDs to the rendered DOM elements.
 * Call this after the content HTML has been mounted to the DOM.
 */
export function applyHeadingIds(
  container: HTMLElement,
  headings: TocHeading[],
): void {
  const elements = container.querySelectorAll("h2, h3, h4")
  elements.forEach((el, i) => {
    const heading = headings[i]
    if (heading) {
      el.id = heading.id
    }
  })
}
